import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Search, Camera, CheckCircle2, PanelLeftClose, PanelLeftOpen, CalendarDays, Users } from 'lucide-react';

const VISIT_TYPE_DOT = {
  'Follow-up': '#22c55e',
  'New Patient': '#3b82f6',
  'Sick Visit': '#f59e0b',
  'Wellness': '#8b5cf6',
  'Annual Physical': '#8b5cf6',
  'Chart Review': '#9ca3af',
};

const VISIT_DURATIONS = {
  'Follow-up': '30 min',
  'New Patient': '60 min',
  'Sick Visit': '20 min',
  'Annual Physical': '45 min',
  'Wellness': '45 min',
  'Chart Review': '—',
};

const HIGH_ALERT_PATIENTS = new Set(['P001', 'P003']);

// Mock risk score history (last 6 visits) — normalized 0-100
const RISK_SPARKLINES = {
  'P001': [62, 68, 72, 70, 74, 78],  // trending up
  'P002': [45, 42, 40, 38, 36, 35],  // trending down
  'P003': [80, 85, 82, 88, 90, 92],  // high and rising
  'P004': [55, 58, 60, 62, 58, 60],  // stable
  'P005': [70, 75, 78, 80, 82, 85],  // trending up
  'P006': [30, 32, 28, 30, 35, 38],  // low and stable
  'P007': [50, 55, 60, 58, 62, 65],  // gradual increase
  'P008': [25, 28, 22, 26, 24, 28],  // low
  'P009': [65, 68, 72, 74, 70, 72],  // fluctuating
  'P010': [40, 45, 48, 50, 52, 55],  // gradual increase
  'P011': [75, 78, 80, 82, 85, 88],  // high and rising
  'P012': [35, 38, 36, 34, 32, 30],  // decreasing
};

// Mock scheduling conflicts
const SCHEDULING_CONFLICTS = {
  'P001': { type: 'overdue', text: 'No eye exam in 14 months' },
  'P005': { type: 'gap', text: 'Last visit 4 months ago — overdue' },
  'P009': { type: 'overdue', text: 'A1C recheck overdue 2 months' },
  'P011': { type: 'gap', text: 'No follow-up scheduled after discharge' },
};

// Mini sparkline SVG renderer
const MiniSparkline = ({ data, width = 36, height = 14 }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);
  const points = data.map((v, i) => `${i * stepX},${height - ((v - min) / range) * (height - 2) - 1}`).join(' ');
  const lastVal = data[data.length - 1];
  const color = lastVal >= 75 ? '#ef4444' : lastVal >= 50 ? '#f59e0b' : '#22c55e';
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="shrink-0">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const LEGEND_ITEMS = [
  { color: '#22c55e', label: 'Follow-up' },
  { color: '#3b82f6', label: 'New patient' },
  { color: '#f59e0b', label: 'Sick visit' },
  { color: '#8b5cf6', label: 'Wellness' },
  { color: '#cccccc', label: 'Other' },
];

// ── Patient Row ──
const PatientRow = ({ apt, isActive, onSelect }) => {
  const isDone = apt.status === 'completed';
  const isInProg = apt.status === 'in_progress';
  const dotColor = VISIT_TYPE_DOT[apt.type] || '#cccccc';
  const duration = VISIT_DURATIONS[apt.type] || '—';
  const hasHighAlert = HIGH_ALERT_PATIENTS.has(apt.patientId);
  const sparkData = RISK_SPARKLINES[apt.patientId];
  const conflict = SCHEDULING_CONFLICTS[apt.patientId];

  const borderLeft = isDone
    ? '3px solid #22c55e'
    : isInProg || isActive
      ? '3px solid #6b5ce7'
      : '3px solid transparent';
  const bg = isActive || isInProg ? '#f5f3ff' : 'transparent';

  return (
    <button
      onClick={() => onSelect(apt)}
      className="w-full text-left transition-all hover:bg-[#fafafa]"
      style={{ padding: '8px 14px', borderLeft, background: bg, opacity: isDone ? 0.55 : 1 }}
      data-testid={`sidebar-patient-${apt.id}`}
    >
      <div className="flex items-start gap-2">
        <div className="shrink-0" style={{ width: 44 }}>
          <p style={{ fontSize: 12, fontWeight: 500, color: isActive || isInProg ? '#6b5ce7' : '#111111' }}>{apt.time}</p>
          <p style={{ fontSize: 10, color: '#aaaaaa' }}>{duration}</p>
        </div>
        <div className="shrink-0" style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, marginTop: 5 }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <p className="flex-1 min-w-0" style={{ fontSize: 12, fontWeight: 500, color: '#111111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{apt.name}</p>
            {isDone && <CheckCircle2 className="h-2.5 w-2.5 text-emerald-500 shrink-0" />}
            {hasHighAlert && !isDone && (
              <div className="shrink-0" style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }} />
            )}
            {sparkData && <MiniSparkline data={sparkData} />}
          </div>
          <p style={{ fontSize: 11, color: '#888888', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{apt.chief}</p>
          {conflict && (
            <p style={{ fontSize: 10, color: conflict.type === 'overdue' ? '#ef4444' : '#d97706', marginTop: 2, lineHeight: 1.2 }}>
              {conflict.type === 'overdue' ? '!' : '⏰'} {conflict.text}
            </p>
          )}
        </div>
      </div>
    </button>
  );
};

const ClinicalFocusSidebar = ({
  schedulePatients,
  allPatients,
  activePatientId,
  onSelectPatient,
  collapsed,
  onToggle,
  onUploadSchedule,
}) => {
  const [activeTab, setActiveTab] = useState('schedule');
  const [searchTerm, setSearchTerm] = useState('');
  const [width, setWidth] = useState(232);
  const isResizing = useRef(false);
  const sidebarRef = useRef(null);

  // Resize handler
  const startResize = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing.current) return;
      const sidebar = sidebarRef.current;
      if (!sidebar) return;
      const rect = sidebar.getBoundingClientRect();
      const newWidth = Math.max(180, Math.min(400, e.clientX - rect.left));
      setWidth(newWidth);
    };
    const handleMouseUp = () => {
      if (isResizing.current) {
        isResizing.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const currentList = activeTab === 'schedule' ? schedulePatients : allPatients;
  const completed = currentList.filter(a => a.status === 'completed').length;
  const inProgress = currentList.filter(a => a.status === 'in_progress').length;
  const remaining = currentList.length - completed - inProgress;

  const dateLabel = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const filtered = useMemo(() => {
    if (activeTab !== 'patients' || !searchTerm.trim()) return currentList;
    const q = searchTerm.toLowerCase();
    return allPatients.filter(p => p.name.toLowerCase().includes(q) || (p.chief || '').toLowerCase().includes(q));
  }, [activeTab, allPatients, currentList, searchTerm]);

  const displayList = activeTab === 'patients' ? filtered : currentList;

  if (collapsed) {
    return (
      <div className="w-14 shrink-0 bg-white flex flex-col items-center py-3 border-r" style={{ borderColor: '#e8e8e8' }} data-testid="sidebar-collapsed">
        <button onClick={onToggle} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors mb-4" data-testid="expand-sidebar-btn">
          <PanelLeftOpen className="h-4 w-4" />
        </button>
        <div className="flex-1 flex flex-col items-center gap-1 overflow-y-auto w-full px-1">
          {currentList.slice(0, 15).map(apt => {
            const isActive = activePatientId === apt.patientId;
            const initials = apt.name.split(' ').map(n => n[0]).join('');
            return (
              <button key={apt.id} onClick={() => onSelectPatient(apt)}
                className={cn("w-10 h-10 rounded-full flex items-center justify-center text-[10px] font-bold transition-all shrink-0",
                  isActive ? "bg-[#6b5ce7] text-white" : apt.status === 'completed' ? "bg-emerald-50 text-emerald-600 opacity-55" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                )} title={apt.name}>
                {initials}
              </button>
            );
          })}
        </div>
        <div className="text-[9px] text-gray-400 font-mono mt-2">{completed}/{currentList.length}</div>
      </div>
    );
  }

  return (
    <div ref={sidebarRef} className="shrink-0 bg-white flex flex-col h-full border-r relative" style={{ width, borderColor: '#e8e8e8' }} data-testid="clinical-sidebar">
      {/* Header */}
      <div style={{ padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0' }}>
        <div className="flex items-center justify-between">
          <div>
            <span style={{ fontSize: 10, fontWeight: 500, color: '#000', background: '#f0f0f0', padding: '1px 8px', borderRadius: 12 }}>Clinical Focus mode</span>
            <p style={{ fontSize: 10, color: '#aaaaaa', marginTop: 2 }}>{dateLabel} &middot; {displayList.length}/{currentList.length}</p>
          </div>
          <button onClick={onToggle} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" data-testid="collapse-sidebar-btn">
            <PanelLeftClose className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Tab Switcher */}
      <div style={{ padding: '6px 14px', borderBottom: '0.5px solid #f0f0f0' }} data-testid="sidebar-tabs">
        <div className="flex rounded-lg p-0.5" style={{ background: '#f5f5f5' }}>
          <button
            onClick={() => setActiveTab('schedule')}
            className="flex-1 flex items-center justify-center gap-1.5 py-1 rounded-md transition-all"
            style={{
              fontSize: 11, fontWeight: 500,
              background: activeTab === 'schedule' ? '#fff' : 'transparent',
              color: activeTab === 'schedule' ? '#111' : '#999',
              boxShadow: activeTab === 'schedule' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
            }}
            data-testid="tab-schedule"
          >
            <CalendarDays className="h-3 w-3" /> Schedule
          </button>
          <button
            onClick={() => setActiveTab('patients')}
            className="flex-1 flex items-center justify-center gap-1.5 py-1 rounded-md transition-all"
            style={{
              fontSize: 11, fontWeight: 500,
              background: activeTab === 'patients' ? '#fff' : 'transparent',
              color: activeTab === 'patients' ? '#111' : '#999',
              boxShadow: activeTab === 'patients' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
            }}
            data-testid="tab-patients"
          >
            <Users className="h-3 w-3" /> Patients
          </button>
        </div>
      </div>

      {/* Search (patients tab only) */}
      {activeTab === 'patients' && (
        <div style={{ padding: '6px 14px', borderBottom: '0.5px solid #f0f0f0' }}>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search patients..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full h-7 pl-7 pr-2 rounded-md border text-xs focus:outline-none focus:ring-1 focus:ring-gray-300"
              style={{ fontSize: 11, borderColor: '#e8e8e8' }}
              data-testid="sidebar-patient-search"
            />
          </div>
        </div>
      )}

      {/* Color Legend */}
      <div style={{ padding: '6px 14px', borderBottom: '0.5px solid #f0f0f0' }} data-testid="visit-type-legend">
        <div className="flex flex-wrap" style={{ gap: 10 }}>
          {LEGEND_ITEMS.map(item => (
            <div key={item.label} className="flex items-center" style={{ gap: 4 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: item.color }} />
              <span style={{ fontSize: 10, color: '#888888' }}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Patient List */}
      <div className="flex-1 overflow-y-auto" data-testid="sidebar-patient-list">
        {displayList.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <p style={{ fontSize: 11, color: '#aaaaaa' }}>No patients found</p>
          </div>
        ) : displayList.map(apt => (
          <PatientRow key={apt.id} apt={apt} isActive={activePatientId === apt.patientId} onSelect={onSelectPatient} />
        ))}
      </div>

      {/* Footer */}
      <div style={{ borderTop: '0.5px solid #f0f0f0', padding: '10px 12px' }} data-testid="sidebar-footer">
        <p style={{ fontSize: 11 }}>
          <span style={{ fontWeight: 500, color: '#111' }}>{completed}</span>
          <span style={{ color: '#aaaaaa' }}> done &middot; </span>
          <span style={{ fontWeight: 500, color: '#111' }}>{inProgress}</span>
          <span style={{ color: '#aaaaaa' }}> active &middot; </span>
          <span style={{ fontWeight: 500, color: '#111' }}>{remaining}</span>
          <span style={{ color: '#aaaaaa' }}> left</span>
        </p>
        <button
          onClick={onUploadSchedule}
          className="w-full flex items-center justify-center gap-2 mt-2"
          style={{
            height: 32, borderRadius: 7, background: '#6b5ce7', color: '#ffffff',
            fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer',
          }}
          data-testid="sidebar-scan-lens-btn"
        >
          <Camera className="h-3.5 w-3.5" /> Scan with Lens
        </button>
      </div>

      {/* Resize Handle */}
      <div
        onMouseDown={startResize}
        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-violet-300 transition-colors"
        style={{ background: 'transparent' }}
        data-testid="sidebar-resize-handle"
      >
        <div className="absolute top-1/2 -translate-y-1/2 right-0 w-1 h-8 rounded-full bg-gray-300 opacity-0 hover:opacity-100 transition-opacity" />
      </div>
    </div>
  );
};

export default ClinicalFocusSidebar;
